import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

// CONFIGS
import { environment } from '@app/env-service';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { IAppIntegrationLocation } from 'v2/modules/app-integration-account/intefaces/app-integration-locations.interface';
import { EAppAccountType } from 'v2/modules/system-setting/constants';

import { SnackBarConfig } from '../../../shared/configs';

// CONSTANTS

// INTERFACES
import {
  IAppIntegrationAccount,
  IIdName,
  IWarehouse,
  IWarehouseGroup,
} from '../../../shared/interfaces';
import {
  LocationWarehouseGroupApiService,
  LocationWarehouseApiService,
} from '../../../shared/services';
import { trackById } from '../../../shared/util';
import { EPickAndPackEnabledValue } from '../../interfaces/location.interface';

// SERVICES

import { AppIntegrationAccountApiService } from './../../../shared/services/api/app-integration/app-integration-account-api.service';

// UTILS

@Component({
  selector: 'lum-location-warehouse-group-popup',
  templateUrl: './location-warehouse-group-popup.component.html',
  styleUrls: ['./location-warehouse-group-popup.component.scss'],
})
export class LocationWarehouseGroupPopupComponent implements OnInit, OnDestroy {
  form :UntypedFormGroup;
  warehouses$ :Observable<IWarehouse[]>;
  warehouses :IWarehouse[] = [];
  selectedWarehouses :IWarehouse[] = [];
  submittingForm$ :Observable<boolean>;
  mode :string = 'Create';
  trackById = trackById;
  webhookUrl :string = '';
  isEdi :boolean = false;
  loadingAppIntegrationAccounts :boolean = false;
  appIntegrationAccountParams :Record<string, any> = {};

  ePickAndPackEnabledValue = EPickAndPackEnabledValue;
  appIntegrationAccounts :IAppIntegrationAccount[] = [];
  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data :{
      warehouseGroup :IWarehouseGroup;
      ftPickAndPackEnabled :boolean;
    },
    public dialogRef :MatDialogRef<LocationWarehouseGroupPopupComponent>,
    private fb :UntypedFormBuilder,
    private warehouseGroupService :LocationWarehouseGroupApiService,
    private warehouseService :LocationWarehouseApiService,
    private appIntegrationAccountApiService :AppIntegrationAccountApiService,
    private snackbar :MatSnackBar,
    private _feature :FeatureToggleService,
  ) {}

  ngOnInit() :void {
    this.initFormData();
    this.getDataFromService();
    this.loadDataFromService();
    this.getWarehouses().subscribe();

    this._updateIsEdi();
    this._updateWebhookUrl();

    // Subscribe to externallyManagedAppIntegrationAccountId changes
    this.form
      .get('externallyManagedAppIntegrationAccountId')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedAccountId) => {
        this._updateIsEdi(selectedAccountId);
      });

    // Subscribe to appIntegrationAccountInventoryLocation changes
    this.form
      .get('appIntegrationAccountInventoryLocation')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedLocation :IAppIntegrationLocation) => {
        this.form.patchValue({
          externallyManagedWarehouse: selectedLocation?.locationId || null,
          appIntegrationAccountInventoryLocationId: selectedLocation?.id || null,
        });
      });
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSubmit() :void {
    const payload = {
      ...this.form.value,
      warehouseIds: this.selectedWarehouseIds,
      relations: 'warehouses',
    };

    if (
      !payload.externallyManagedAppIntegrationAccountId &&
      payload.externallyManagedAppIntegrationAccountId < 0
    ) {
      payload.externallyManagedAppIntegrationAccountId = 0;
    }

    if (this.isEdi && payload.externallyManagedWarehouse == null) {
      payload.externallyManagedWarehouse = 'false';
    }

    delete payload.appIntegrationAccountInventoryLocation; // don't include in payload

    if (this.data?.warehouseGroup?.id) {
      this.warehouseGroupService
        .update(this.data.warehouseGroup.id, payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            this.dialogRef.close(true);
          },
          error: (error) => {
            this.snackbar.open(error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    } else {
      this.warehouseGroupService
        .create(payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            this.dialogRef.close(true);
          },
          error: (error) => {
            this.snackbar.open(error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    }
  }

  onWarehouseSelect() :void {
    if (this.defaultWarehouseId) {
      const defaultWarehouseSelected = this.selectedWarehouses.find(
        (warehouse) => warehouse.id === this.defaultWarehouseId,
      );

      this.form.patchValue({
        warehouseIds: this.selectedWarehouses,
        ...(!defaultWarehouseSelected && { defaultWarehouseId: null }), // set to null if not available in selected warehouses
      });
    }

    this.form.patchValue({ warehouseIds: this.selectedWarehouses });
  }

  compareWarehouses(object1 :IWarehouse, object2 :IWarehouse) :boolean {
    return object1.id === object2.id;
  }

  handleWebhookCopied(didCopy :boolean) :void {
    if (didCopy) {
      this.snackbar.open('Copied Webhook URL to Clipboard', 'Success', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
    }
  }

  onLazyLoadAppIntegrationAccount() :void {
    let page = this.appIntegrationAccountParams?.page ?? 1;
    const lastPage = this.appIntegrationAccountParams?.last_page ?? 0;
    if (lastPage > page) {
      this.appIntegrationAccountParams.page = ++page;
      this.loadAppIntegrationAccounts({
        page: this.appIntegrationAccountParams.page,
      });
    }
  }

  onSelectAppIntegrationAccountEvent(
    appIntegrationAccountEvent :IAppIntegrationAccount,
  ) :void {
    const appIntegrationAccountValue = this.appIntegrationAccounts.find(
      (account) => account.id === appIntegrationAccountEvent?.id,
    );

    if (appIntegrationAccountValue) {
      this.externallyManagedAppIntegrationAccountIdControl?.patchValue(
        appIntegrationAccountValue?.id,
      );
    }
  }

  onSearchAppIntegrationAccounts(label :string) :void {
    this.appIntegrationAccountParams = {};
    this.appIntegrationAccounts = [];
    this.loadAppIntegrationAccounts({ label });
  }

  private initFormData() :void {
    this.form = this.fb.group({
      label: ['', Validators.required],
      description: [''],
      warehouseIds: [[], Validators.required],
      defaultWarehouseId: [null, Validators.required],
      pickAndPackEnabled: [null],
      scanToVerify: [false, Validators.required],
      externallyManaged: [false],
      calculateKitQuantities: [false],
      externallyManagedAppIntegrationAccountId: [0],
      externallyManagedWarehouse: [null],
      appIntegrationAccountInventoryLocation: [null],
      appIntegrationAccountInventoryLocationId: [null],
      pullStocks: [false],
      pullShipments: [false],
      combineKitInventory: [false],
    });
    if (this.data?.warehouseGroup) {
      this.mode = 'Update';
      const {
        label,
        description,
        warehouses,
        defaultWarehouse,
        pickAndPackEnabled,
        scanToVerify,
        externallyManaged,
        calculateKitQuantities,
        externallyManagedAppIntegrationAccountId,
        externallyManagedWarehouse,
        pullStocks,
        pullShipments,
        combineKitInventory,
        appIntegrationAccountInventoryLocation,
        appIntegrationAccountInventoryLocationId,
      } = this.data.warehouseGroup;
      this.selectedWarehouses = warehouses.map((warehouse) => {
        return { id: warehouse.id, location_name: warehouse.locationName };
      });
      this.form.patchValue({
        label,
        description,
        scanToVerify,
        warehouseIds: this.selectedWarehouses,
        defaultWarehouseId: defaultWarehouse ? defaultWarehouse.id : null,
        pickAndPackEnabled: pickAndPackEnabled,
        externallyManaged: externallyManaged,
        calculateKitQuantities: calculateKitQuantities || false,
        externallyManagedWarehouse,
        externallyManagedAppIntegrationAccountId,
        appIntegrationAccountInventoryLocation,
        appIntegrationAccountInventoryLocationId,
        pullStocks,
        pullShipments,
        combineKitInventory,
      });
    }
  }

  private loadDataFromService() :void {
    this.loadWarehouses();
    this.loadAppIntegrationAccounts();
  }

  private getDataFromService() :void {
    this.warehouses$ = this.warehouseService.warehouses$;
    this.submittingForm$ = this.warehouseGroupService.submittingForm$;
  }

  private loadWarehouses() :void {
    const warehouseParams :{
      all :string;
      parent_id :string;
      withoutWarehouseGroup ?:string;
      warehouseGroupId ?:string;
    } = {
      all: '1',
      parent_id: '0',
    };

    if (
      this._feature.isActive(
        'red_DV-3094_multiple_warehouses_on_different_warehouse_groups',
      ) === false
    ) {
      warehouseParams.withoutWarehouseGroup = '1';
    }

    if (this.data?.warehouseGroup) {
      warehouseParams.warehouseGroupId = this.data.warehouseGroup.id.toString();
    }

    this.warehouseService.fetchWarehouses(warehouseParams);
  }

  private loadAppIntegrationAccounts(
    params :{ [key :string] :string | string[] } = {},
  ) :void {
    this.loadingAppIntegrationAccounts = true;

    this.appIntegrationAccountApiService
      .get({
        actions: 'pullStocks,getInboundShipments',
        ...params,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response?.data?.length > 0) {
            const existingData = this.appIntegrationAccounts || [];
            let newData = response.data;

            if (!params.label) {
              const externallyManagedAppIntegrationAccount =
                this.data?.warehouseGroup?.externallyManagedAppIntegrationAccount;

              if (externallyManagedAppIntegrationAccount) {
                // Remove duplicates of externallyManagedAppIntegrationAccount from newData
                newData = newData.filter(
                  (account) =>
                    account.id !== externallyManagedAppIntegrationAccount.id,
                );

                // Add externallyManagedAppIntegrationAccount to the newData
                newData = [externallyManagedAppIntegrationAccount, ...newData];
              }
            }

            // Merge existing data with newData while removing duplicates
            this.appIntegrationAccounts = [
              ...existingData.filter(
                (existing) =>
                  !newData.some((newAccount) => newAccount.id === existing.id),
              ),
              ...newData,
            ];
            this.loadingAppIntegrationAccounts = false;
          }

          if (response?.meta) {
            this.appIntegrationAccountParams.last_page = response?.meta?.last_page;
          }
          this._updateIsEdi();
          this._updateWebhookUrl();
        },
        error: (error) => {
          this.snackbar.open(
            error?.message || 'Failed to load app integration accounts',
            'Fail',
          );
        },
      });
  }

  private getWarehouses() :Observable<IWarehouse[]> {
    return this.warehouses$.pipe(
      tap((warehouses) => {
        this.warehouses = warehouses;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  /**
   * Sets `this.isEdi`.
   * @returns {boolean}  The new value of `this.isEdi`, in case it's useful.
   */
  private _updateIsEdi(selectedAccountId ?:number) {
    if (!selectedAccountId) {
      selectedAccountId = this.form.get(
        'externallyManagedAppIntegrationAccountId',
      )?.value;

      if (selectedAccountId == null) {
        return (this.isEdi = false);
      }
    }

    const selectedAccount = selectedAccountId
      ? this.appIntegrationAccounts.find(({ id }) => id === selectedAccountId)
      : null;

    return (this.isEdi = selectedAccount?.app === EAppAccountType.EDI);
  }
  /**
   * Sets `this.webhookUrl`.
   * @returns {string}  The new value of `this.webhookUrl`, in case it's useful.
   */
  private _updateWebhookUrl() :string {
    if (!this.data?.warehouseGroup?.uuid) {
      return (this.webhookUrl = '');
    }

    return (this.webhookUrl = `${environment.url.replace('/admin', '')}/edi/warehouse/${this.data.warehouseGroup.uuid}`);
  }

  get selectedExternallyManagedAppIntegrationAccountId() :any | null {
    const externallyManagedAppIntegrationAccountId =
      this.externallyManagedAppIntegrationAccountIdControl?.value;
    if (this.externallyManagedAppIntegrationAccountIdControl?.value?.length <= 0) {
      return null;
    }

    return (
      this.appIntegrationAccounts.find(
        (account) => account.id === externallyManagedAppIntegrationAccountId,
      ) ?? null
    );
  }

  get selectedWarehouseIds() :number[] {
    return this.selectedWarehouses.map((warehouse) => warehouse.id);
  }

  get defaultWarehouseId() :number {
    return this.form.get('defaultWarehouseId')?.value;
  }

  get ftPickAndPackEnabled() :boolean {
    return this.data?.ftPickAndPackEnabled || false;
  }

  get warehouseGroup() :IWarehouseGroup {
    return this.data?.warehouseGroup;
  }

  get extWarehouses() :IIdName[] {
    return this.selectedWarehouses.map((warehouse) => ({
      id: warehouse.id,
      name: warehouse.location_name,
    }));
  }

  get externallyManagedAppIntegrationAccountIdControl() :AbstractControl | null {
    return this.form.get('externallyManagedAppIntegrationAccountId');
  }

  get isEnableExteralManage() :boolean {
    return this.form.get('externallyManaged')?.value;
  }

  get selectedAppIntegrationAccount() :IAppIntegrationAccount {
    const id = this.form.get('externallyManagedAppIntegrationAccountId')?.value;

    return this.appIntegrationAccounts.find((account) => account.id === id);
  }
}
